import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, Self} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AbstractControl, ControlValueAccessor, FormControl, FormRecord, NgControl, ReactiveFormsModule, ValidationErrors, ValidatorFn} from "@angular/forms";
import {TranslateModule} from "@ngx-translate/core";
import {MatCheckbox} from "@angular/material/checkbox";
import {MedlemSkjemaKomponentAvhengigheter_DI_TOKEN} from "../medlem-skjema/medlem-skjema";
import {muligeTilgangsrollerForMedlem, TilgangsrollerMedlemInfo} from "felles-ng2/modell/medlemModellHjelpere";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {Mobilnummer, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {enumInnslag, navnPåAlleEnuminnslag, navnPåEnuminnslag} from "felles-ng2/støttefunksjoner/enumer";
import {deaktiverFormKontroller} from "felles-ng2/infrastruktur/angular/reactiveForm";

const debug = initDebugger('MedlemRedigerTilgangsrollerKomponent');


@Component({
    selector: 'medlem-rediger-tilgangsroller',
    templateUrl: './medlem-rediger-tilgangsroller.html',
    styleUrl: './medlem-rediger-tilgangsroller.scss',
    providers: [{ provide: MedlemSkjemaKomponentAvhengigheter_DI_TOKEN, useExisting: MedlemRedigerTilgangsrollerKomponent }],
    imports: [
        ReactiveFormsModule,
        MatCheckbox,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemRedigerTilgangsrollerKomponent implements OnInit, ControlValueAccessor {

    skjema = new FormRecord<FormControl<boolean>>({});
    tilgjengeligeTilgangsroller!: { rolleinfo: TilgangsrollerMedlemInfo, kontroller: FormControl<boolean> }[];

    constructor(/** skjemakontrollen tilknyttet denne komponenten via formControl eller ngModel direktiver*/
                @Self() private tilknyttetSkjemakontroll: NgControl,
                public enumOversettelser: EnumOversettelser,
                private destroyRef: DestroyRef) {
        // setter valueAccessor direkte fremfor via DI og providers i @Component med `{provide: NG_VALUE_ACCESSOR, multi:true, useExisting: MobilnummerFeltKomponent}`
        // for å unngå sirkulær avhengighet.
        this.tilknyttetSkjemakontroll.valueAccessor = this;

        const muligeTilgangsroller = muligeTilgangsrollerForMedlem();

        muligeTilgangsroller.forEach(rolleinfo =>
          this.skjema.addControl(rolleinfo.enumNavn, new FormControl(rolleinfo.obligatorisk, {nonNullable: true})));

        //init tilgjengeligeTilgangsroller
        this.tilgjengeligeTilgangsroller = muligeTilgangsroller
          .map(rolleinfo => {
              const kontroller = this.skjema.controls[rolleinfo.enumNavn];
              if (rolleinfo.obligatorisk) kontroller.disable();
              return {rolleinfo, kontroller};
          });

        //lytt til endringer
        this.skjema.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(valgte => {
              //reset disabled på alle - slik at disabled ikke blir hengende igjen på roller som ikke lenger er implisert
              this.tilgjengeligeTilgangsroller
                .forEach(rolle => deaktiverFormKontroller(rolle.rolleinfo.obligatorisk, rolle.kontroller, {emitEvent: false}));

              //sørg for å legge til impliserte roller, samt sørge for at disse ikke kan fjernes
              this.aktiverImpliserteRoller();

              //eksponert verdi via ControlValueAccessor
              const valgteRoller = this.lesTilgangsroller()
              debug("eksponerer verdi", valgteRoller, this.skjema.valid, this.skjema.getRawValue())
              if (erIkkeNil(this.vedEndring)) this.vedEndring(valgteRoller);
          });
    }


    //ng2 livssykelmetoder

    ngOnInit(): void {
        //sørger for at valideringsfeil knyttet til det lokale skjemaet propagerer til skjemakontroll tilknyttet denne komponenten
        //kan ikke gjøres i konstruktør da tilknyttetSkjemakontroll.control ikke er satt enda
        this.tilknyttetSkjemakontroll.control!.addValidators(this.tilknyttetSkjemakontrollValidator())
    }

    /** Returnerer valideringsfeil registrert på {@link #skjema}.
     * Se også {@link #propagerValideringsfeilTilTilknyttetSkjemakontroll} */
    private tilknyttetSkjemakontrollValidator(): ValidatorFn {
        return (control: AbstractControl<Mobilnummer>): ValidationErrors | null => {
            //les valideringsfeil fra feltKontroll og returner disse
            debug("tilknyttetSkjemakontrollValidator", this.skjema.valid, this.skjema.errors)
            return this.skjema.errors;
        }
    }


    //ControlValueAccessor implementasjon

    /** Settes via {@link #registerOnChange} (ifbm ControlValueAccessor)*/
    private vedEndring: Function | null = null;

    /** Settes via {@link #registerOnTouched} (ifbm ControlValueAccessor)*/
    private onTouched: Function | null = null;

    registerOnChange(vedEndring: Function): void {
        this.vedEndring = vedEndring;
    }

    registerOnTouched(vedBerørt: Function): void {
        this.onTouched = vedBerørt;
    }

    setDisabledState(isDisabled: boolean): void {
        debug("setDisabledState(%s), støttes pt ikke", isDisabled)
    }

    writeValue(tilgangsroller: TilgangsrolleMedlem[] | null): void {
        debug("writeValue", tilgangsroller)
        this.setTilgangsroller(tilgangsroller ?? []);
    }


    //private hjelpemetoder

    private lesTilgangsroller(): TilgangsrolleMedlem[] {
        const verdier = this.skjema.getRawValue();
        return navnPåAlleEnuminnslag(TilgangsrolleMedlem)
          .filter(rollenavn => verdier[rollenavn])
          .map(rollenavn => enumInnslag<TilgangsrolleMedlem>(rollenavn, TilgangsrolleMedlem));
    }

    private setTilgangsroller(aktiverteRoller: TilgangsrolleMedlem[]): void {
        this.skjema.reset({}, {emitEvent: false});
        aktiverteRoller
          .map(rolle => navnPåEnuminnslag(rolle, TilgangsrolleMedlem))
          .forEach(rollenavn => this.skjema.controls[rollenavn].setValue(true, {emitEvent: false}));

        //sørg for at impliserte roller ikke kan fjernes
        this.aktiverImpliserteRoller(false);
    }

    /** Finn aktiverte roller som impliserer andre roller og aktiver disse.
     * For hver impliserte rolle blir
     * - skjemakontrollen markert som deaktivert slik at bruker ikke kan velge bort rollen
     * - rollen markert som valgt (kun dersom oppdaterValgtStatus == true)
     */
    private aktiverImpliserteRoller(oppdaterValgtStatus = true) {
        //finn aktiverte roller som impliserer andre roller og aktiver disse
        this.tilgjengeligeTilgangsroller
          .filter(rolle => rolle.kontroller.value && erIkkeNil(rolle.rolleinfo.impliserer)) //kun aktiverte roller som impliserer andre roller
          .map(rolle => this.tilgjengeligeTilgangsroller.find(r => r.rolleinfo.rolle == rolle.rolleinfo.impliserer)!)
          .forEach(rolle => {
              if (oppdaterValgtStatus) rolle.kontroller.setValue(true, {emitEvent: false})
              rolle.kontroller.disable({emitEvent: false});
          });
    }
}
